import type { etatDesLieux as Fr } from '../fr/etatDesLieux';

/** « etatDesLieux » in English — typed on the French: a missing sentence does not compile. */
export const etatDesLieux: typeof Fr = {
  accessoires: {
    cle: 'Key', carteGrise: 'Registration document', assurance: 'Insurance', vignette: 'Tax disc',
    controleTechnique: 'Technical inspection', giletTriangle: 'Vest & warning triangle',
    crick: 'Jack', roueSecours: 'Spare wheel',
  } as Record<string, string>,
  typesDommage: { rayure: 'scratch', choc: 'dent', eclat: 'chip', manquant: 'missing' } as Record<string, string>,
  zones: { avant: 'front', arriere: 'rear', cote_gauche: 'left side', cote_droit: 'right side' } as Record<string, string>,
  carburantQuarts: ['empty', '1/4', '1/2', '3/4', 'F'],
  supplements: {
    retard: 'Late return (article 02)',
    carburant_manquant: 'Missing fuel (article 04)',
    km_supplementaire: 'Extra mileage (article 05)',
    nettoyage: 'Partial cleaning (article 03)',
  } as Record<string, string>,
  libelleFile: (retour: boolean, numero: string) => `${retour ? 'Check-in' : 'Check-out'} inspection — ${numero}`,
  envoiPhotos: (faites: number, total: number) => `Uploading photos ${faites}/${total}…`,
  erreurEnregistrement: 'Could not save',
  rendus: (m: string) => `${m} refunded to the customer — the entry is in the cash register.`,
  erreurRemboursement: 'The refund did not go through.',
  horsLigneTitre: 'Saved offline',
  fileTitre: 'Queued',
  horsLigneTexte: (n: number) => `You have no network. The inspection and its ${n} ${n === 1 ? 'photo' : 'photos'} are kept on this phone and will be sent automatically as soon as the connection is back. You can keep working.`,
  revenirTaches: 'Back to my tasks',
  retourEnregistre: 'Check-in saved',
  nouveauxDommages: (n: number) => `${n} new ${n === 1 ? 'damage' : 'damages'}`,
  nouveauxDommagesTexte: 'Found at check-in, not present at check-out. This comparison is what prevents disputes.',
  supplementsAFacturer: 'Extra charges to invoice',
  nonChiffrable: 'Check-in CANNOT be priced — do not refund the deposit',
  absentsCatalogue: 'These extra charges required by your rental agreement are missing from the catalogue, so they were never calculated:',
  gerantDoitA: 'A manager must fill them in under ',
  parametresSupplements: 'Settings › Extra charges',
  gerantDoitB: ' before settling this check-in.',
  totalIncompletA: ' The total shown below is ',
  incomplet: 'incomplete',
  totalIncompletB: '.',
  aucunSupplement: 'No extra charges',
  conforme: 'Vehicle returned in order.',
  colSupplement: 'Extra charge', colCalcul: 'Calculation', colMontant: 'Amount',
  totalSupplements: 'Total extra charges',
  retourAnticipe: (n: number) => `Early return — ${n} ${n === 1 ? 'day' : 'days'} unused`,
  finPrevue: (d: string) => `Scheduled end ${d}`,
  verseA: (m: string) => `The customer paid ${m}. For the days not used, the software suggests refunding`,
  verseB: '. You decide the amount, or refund nothing.',
  montantARendre: (devise: string) => `Amount to refund (${devise})`,
  motifObligatoire: 'Reason (required)',
  motifPlaceholder: 'Early return — 3 days unused',
  rendre: (m: string) => `Refund ${m}`,
  largent: 'the money',
  aucunRemboursement: 'No refund — the full rental remains due.',
  neRienRendre: 'Refund nothing',
  anticipeTraite: 'Early return handled',
  voirDossier: 'View the file',
  encaisserSolde: 'Take payment of the balance',
  titre: (retour: boolean) => `Inspection — ${retour ? 'check-in' : 'check-out'}`,
  saisieEnCours: 'An entry is already in progress on this device',
  etiezA: 'You were at ',
  etapeSur: (n: number) => `step ${n} of 5`,
  avec: ', with ',
  photosN: (n: number) => `${n} ${n === 1 ? 'photo' : 'photos'}`,
  virgule: ', ',
  dommagesN: (n: number) => `${n} ${n === 1 ? 'damage' : 'damages'}`,
  releves: ' recorded',
  et: ', and ',
  laSigClient: 'the customer signature',
  celleAgent: "the agent's",
  joinEt: ' and ',
  point: '.',
  reprendre: 'Resume where I left off',
  recommencer: 'Start over',
  etape: (n: number) => `Step ${n} of 5`,
  auDepart: 'At check-out',
  resumeDepart: (date: string, km: string, u: string, carb: string, pi: number, pe: number, n: number) =>
    `${date} · ${km} ${u} · fuel ${carb} · cleanliness ${pi}/10 int. ${pe}/10 ext. · ${n} ${n === 1 ? 'damage' : 'damages'} noted`,
  photos: 'Photos',
  dommages: 'Damage',
  pointsGris: 'The grey dots are the ones noted at check-out.',
  typeDommage: 'Type of damage',
  commentaire: 'Comment',
  commentairePlaceholder: 'Scratch on front right door',
  annuler: 'Cancel',
  ajouter: 'Add',
  retirer: 'remove',
  carburantKmProprete: 'Fuel, mileage and cleanliness',
  dateRetour: 'Check-in date and time',
  dateDepart: 'Check-out date and time',
  momentAide: 'The actual time, not the time of entry. It is what counts for a late return.',
  retardTitre: (heures: number, montant: string) =>
    `${heures} hour(s) of late return will be charged${montant ? ` — ${montant}` : ''}`,
  retardDetail: (du: string, saisi: string) =>
    `The car was due on ${du}. You are recording a return on ${saisi}. If this is not the time the `
    + 'customer actually returned the car — for example because you are entering this return later — '
    + 'correct the time above before confirming.',
  carburant: 'Fuel',
  carburantAide: 'The four boxes of your paper rental agreement (article 04)',
  kmCompteur: 'Odometer reading',
  kmAide: (depart: string, parcourus: string, u: string) => `Check-out: ${depart} ${u} — ${parcourus} ${u} driven`,
  propreteInt: 'Interior cleanliness',
  propreteExt: 'Exterior cleanliness',
  accessoiresTitre: 'Accessories',
  observations: 'Remarks',
  signatures: 'Signatures',
  sigClientLabel: 'Customer signature — preceded by “Read and approved”',
  sigAgentLabel: 'Manager / agent signature',
  sigManquantes: 'Missing signatures',
  deuxManquent: 'Both signatures are missing.',
  clientManque: 'The customer signature is missing.',
  votreManque: 'Your signature is missing.',
  sansElles: 'Without them, this document binds no one in a dispute.',
  aucunePhoto: 'No photos',
  aucunePhotoTexte: 'A photographic inspection is your best protection if damage is disputed. Take at least the four sides at step 4.',
  sansPhoto: 'I am validating without photos, knowingly',
  plusModifier: 'Once saved, you can no longer edit it',
  verifiez: 'Check before validating. If there is a mistake, you will need to file a correction request.',
  retour: 'Back',
  suivant: 'Next',
  enregistrement: 'Saving…',
  validerRetour: 'Validate check-in',
  validerDepart: 'Validate check-out',
  recap: 'Summary',
  kilometrage: 'Mileage',
  proprete: 'Cleanliness',
  propreteValeur: (pi: number, pe: number) => `${pi}/10 int. · ${pe}/10 ext.`,
  accessoiresPresents: 'Accessories present',
  photosPrises: 'Photos taken',
  dommagesMarques: 'Damage marked',
  motClient: 'customer',
  motAgent: 'agent',
};
